import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { readPage } from '../../../utils/apiResult';

// FE-10 — Süreç aşaması yönetimi. Arka uç (v1/ats-pipeline-stages) CRUD'u zaten
// vardı; ekran yoktu. Rotalar (koddan doğrulandı — AtsPipelineStageController):
//   GET    v1/ats-pipeline-stages                       (name[like], type[in], scope[in],
//                                                        jobPostingId[eq], isTerminal[eq],
//                                                        defaultSlaHours[gte|lte], orderIndex[gte|lte])
//   POST   v1/ats-pipeline-stages
//   PUT    v1/ats-pipeline-stages/{id}
//   DELETE v1/ats-pipeline-stages/{id}
//   POST   v1/ats-pipeline-stages/restore-defaults
//   POST   v1/ats-pipeline-stages/clone-to-posting/{jobPostingId}
//   POST   v1/ats-pipeline-stages/revert-posting/{jobPostingId}

// Aşama TİPİ serbest metin DEĞİL: motor tam-string karşılaştırıyor
// (AtsApplicationDataAccess "hired"/"disqualified"/"offer"/"reference_check").
// Bu liste sunucudaki beyaz listenin (AtsPipelineStageDefaults.AllowedTypes)
// birebir aynısı olmalı — ayrışırsa kullanıcı sunucunun reddettiği bir tip seçer.
export const STAGE_TYPES = [
  'screening', 'phone_interview', 'technical', 'panel_interview',
  'reference_check', 'offer', 'hired', 'disqualified', 'custom',
];

// Bir kapsamın kaybedemeyeceği terminal tipler (sunucu da engelliyor) — drawer'da
// uyarı metni bu listeden türer.
export const PROTECTED_TERMINAL_TYPES = ['hired', 'disqualified'];

const ROOT = '/v1/ats-pipeline-stages';

const buildPayload = (values, jobPostingId) => ({
  jobPostingId: jobPostingId || null,
  name: values.name,
  slug: values.slug || null,
  orderIndex: Number(values.orderIndex) || 0,
  color: values.color || null,
  isTerminal: !!values.isTerminal,
  // BP-30: 0 = "SLA yok" GEÇERLİ bir seçim; boş bırakılırsa da 0 gider.
  defaultSlaHours: values.defaultSlaHours === '' || values.defaultSlaHours == null
    ? 0
    : Number(values.defaultSlaHours),
  type: values.type || 'custom',
});

// `jobPostingId` null → kiracı varsayılan listesi; dolu → o ilana özel liste.
const useAtsPipelineStagesApi = (queryParams, jobPostingId) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = {
      ...(queryParams && Object.keys(queryParams).length > 0
        ? queryParams
        : { pageNumber: 1, pageSize: 10 }),
    };
    // Kapsam SUNUCUDA süzülür. Kiracı sekmesinde `scope[in]=tenant_default` olmazsa
    // ilana özel satırlar listeye karışır ve kullanıcı yanlış listeyi düzenler.
    if (jobPostingId) params['jobPostingId[eq]'] = jobPostingId;
    else params['scope[in]'] = 'tenant_default';

    setFetching(true);
    api.get(`${ROOT}?${new URLSearchParams(params)}`)
      .then((res) => {
        // api.js OKUMALARDA reject ETMEZ — gerçek sinyal res.data.status.
        const { ok, items, pagination: p } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(p);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger, jobPostingId]);

  const create = useCallback(async (values) => {
    try {
      await api.post(ROOT, buildPayload(values, jobPostingId));
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh, jobPostingId]);

  const update = useCallback(async (id, values) => {
    try {
      await api.put(`${ROOT}/${id}`, buildPayload(values, jobPostingId));
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh, jobPostingId]);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`${ROOT}/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const restoreDefaults = useCallback(async () => {
    try {
      await api.post(`${ROOT}/restore-defaults`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const cloneToPosting = useCallback(async () => {
    if (!jobPostingId) return false;
    try {
      await api.post(`${ROOT}/clone-to-posting/${jobPostingId}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh, jobPostingId]);

  const revertPosting = useCallback(async () => {
    if (!jobPostingId) return false;
    try {
      await api.post(`${ROOT}/revert-posting/${jobPostingId}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh, jobPostingId]);

  const typeLabel = useCallback(
    (v) => t(`ats.pipelineStage.type.${v}`, { defaultValue: v }),
    [t],
  );

  // Sıralama: backend orderBy switch'i name | type | defaultslahours; diğerleri
  // varsayılan OrderIndex sırasına düşer, o yüzden `sort: false`.
  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'orderIndex',
      label: t('ats.pipelineStage.orderIndex'),
      options: { filter: false, sort: false },
    },
    {
      name: 'name',
      label: t('ats.pipelineStage.name'),
      options: {
        filter: true, sort: true, sortKey: 'name',
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'type',
      label: t('ats.pipelineStage.typeLabel'),
      options: {
        filter: false, sort: true, sortKey: 'type',
        customBodyRender: (v) => typeLabel(v),
      },
    },
    {
      name: 'isTerminal',
      label: t('ats.pipelineStage.isTerminal'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? t('common.yes') : t('common.no')),
      },
    },
    {
      name: 'defaultSlaHours',
      label: t('ats.pipelineStage.defaultSlaHours'),
      options: {
        filter: false, sort: true, sortKey: 'defaultslahours',
        // 0 gerçek bir seçim ("SLA yok"); "0 saat" yazmak yanlış okunurdu.
        customBodyRender: (v) => (v > 0 ? t('ats.pipelineStage.slaHoursValue', { count: v }) : t('ats.pipelineStage.noSla')),
      },
    },
    {
      name: 'openApplicationCount',
      label: t('ats.pipelineStage.openApplicationCount'),
      options: { filter: false, sort: false, customBodyRender: (v) => v ?? 0 },
    },
  ], [t, typeLabel]);

  const typeOptions = useMemo(
    () => STAGE_TYPES.map((id) => ({ id, name: typeLabel(id) })),
    [typeLabel],
  );

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('ats.pipelineStage.name'), type: 'text', grid: 12, required: true },
    {
      name: 'type', label: t('ats.pipelineStage.typeLabel'), type: 'select', grid: 6,
      required: true, options: typeOptions, defaultValue: 'custom',
      description: t('ats.pipelineStage.typeHint'),
    },
    { name: 'orderIndex', label: t('ats.pipelineStage.orderIndex'), type: 'number', grid: 6, required: true },
    {
      name: 'defaultSlaHours', label: t('ats.pipelineStage.defaultSlaHours'), type: 'number', grid: 6,
      description: t('ats.pipelineStage.defaultSlaHoursHint'),
    },
    {
      name: 'isTerminal', label: t('ats.pipelineStage.isTerminal'), type: 'checkbox', grid: 6,
      description: t('ats.pipelineStage.isTerminalHint'),
    },
    { name: 'color', label: t('ats.pipelineStage.color'), type: 'colorPicker', grid: 6 },
    // NOT: `autoAdvanceAfterDays` BİLEREK YOK. Kolon veritabanında duruyor ama
    // hiçbir iş/servis onu OKUMUYOR (grep: yalnız yazma/projeksiyon) — forma
    // koymak "otomatik ilerleme" diye çalışmayan bir söz vermek olurdu.
  ], [t, typeOptions]);

  return {
    fetching, loadFailed, data, pagination, columns, objectSchema, typeLabel,
    refresh, create, update, remove, restoreDefaults, cloneToPosting, revertPosting,
  };
};

export default useAtsPipelineStagesApi;
